import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaEdit,
  FaFilter,
  FaPlus,
  FaSave,
  FaSearch,
  FaSync,
  FaTrash,
  FaLayerGroup,
  FaUserPlus,
  FaUserMinus,
} from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";

const db = getFirestore(firebaseApp);

const EVENTO_DEFAULT = {
  codigo: "",
  nombre: "",
  categoria: "Festividad o Celebración",
  estado: "ACTIVO",
  fecha: new Date().toISOString().split("T")[0],
};

const ESTADOS = ["ACTIVO", "INACTIVO"];
const CATEGORIAS = ["Festividad o Celebración", "Capacitación", "Comedor", "Otro"];

const CATEGORIA_COLORS = {
  "Festividad o Celebración": { bg: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)", badge: "#b45309" },
  "Capacitación":             { bg: "linear-gradient(135deg, #059669 0%, #10b981 100%)", badge: "#065f46" },
  "Comedor":                  { bg: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)", badge: "#5b21b6" },
  "Otro":                     { bg: "linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)", badge: "#374151" },
};

const normalizarCodigo = (valor) =>
  String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getEventosCollection = (rucEmpresa) =>
  collection(db, "eventos", rucEmpresa, "eventos");

const EventosAsistencia = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || usuario?.rucEmpresa || "";

  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editandoCodigo, setEditandoCodigo] = useState(null);
  const [form, setForm] = useState(EVENTO_DEFAULT);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("TODAS");

  const [showInvitadosModal, setShowInvitadosModal] = useState(false);
  const [eventoInvitados, setEventoInvitados] = useState(null);
  const [usersEmpresa, setUsersEmpresa] = useState([]);
  const [loadingInvitados, setLoadingInvitados] = useState(false);
  const [savingInvitado, setSavingInvitado] = useState(false);
  const [loadingCargaMasiva, setLoadingCargaMasiva] = useState(false);
  const [searchInvitado, setSearchInvitado] = useState("");
  const inputCargaMasivaRef = useRef(null);
  const modoCargaMasivaRef = useRef("commit");

  // ── Cargar eventos ──────────────────────────────────────────────────────────
  const cargarEventos = useCallback(async () => {
    if (!rucEmpresa) { setLoading(false); return; }
    setLoading(true);
    try {
      const snap = await getDocs(
        query(getEventosCollection(rucEmpresa), orderBy("fecha", "asc"))
      );
      setEventos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error al cargar eventos:", err);
      Swal.fire("Error", "No se pudieron cargar los eventos.", "error");
    } finally {
      setLoading(false);
    }
  }, [rucEmpresa]);

  useEffect(() => { cargarEventos(); }, [cargarEventos]);

  // ── Modal helpers ───────────────────────────────────────────────────────────
  const handleOpenModal = (evento = null) => {
    if (evento) {
      setEditMode(true);
      setEditandoCodigo(evento.codigo || evento.id);
      setForm({
        codigo:    evento.codigo || evento.id,
        nombre:    evento.nombre || "",
        categoria: evento.categoria || CATEGORIAS[0],
        estado:    evento.estado || "ACTIVO",
        fecha:     evento.fecha || "",
      });
    } else {
      setEditMode(false);
      setEditandoCodigo(null);
      setForm(EVENTO_DEFAULT);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleOpenInvitadosModal = async (evento) => {
    if (!evento || !rucEmpresa) return;

    setEventoInvitados(evento);
    setShowInvitadosModal(true);
    setSearchInvitado("");
    setLoadingInvitados(true);

    try {
      const usersRef = collection(db, "users");
      const usersQ = query(usersRef, where("rucEmpresa", "==", rucEmpresa));
      const usersSnap = await getDocs(usersQ);
      const usersList = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setUsersEmpresa(usersList);
    } catch (err) {
      console.error("Error al cargar invitados del evento:", err);
      Swal.fire("Error", "No se pudo cargar la lista de invitados.", "error");
      setShowInvitadosModal(false);
    } finally {
      setLoadingInvitados(false);
    }
  };

  const handleCloseInvitadosModal = () => {
    setShowInvitadosModal(false);
    setEventoInvitados(null);
    setSearchInvitado("");
  };

  const descargarPlantillaInvitados = () => {
    const codigosDisponibles = eventos
      .map((ev) => String(ev.codigo || ev.id || "").trim())
      .filter(Boolean)
      .slice(0, 2);

    const codigoBase = eventoInvitados?.codigo || eventoInvitados?.id || codigosDisponibles[0] || "";
    const codigoAlterno = codigosDisponibles.find((c) => c !== codigoBase) || codigoBase;

    const ws = XLSX.utils.aoa_to_sheet([
      ["codigo_evento", "dni"],
      [codigoBase, "02834688"],
      [codigoAlterno, "41607220"],
    ]);
    ws["!cols"] = [{ wch: 20 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
    XLSX.writeFile(wb, "plantilla_invitados_evento.xlsx");
  };

  const normalizarHeader = (header) =>
    String(header || "")
      .toLowerCase()
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "_");

  const abrirSelectorCargaMasiva = (modo = "commit") => {
    modoCargaMasivaRef.current = modo;
    if (inputCargaMasivaRef.current) {
      inputCargaMasivaRef.current.value = "";
      inputCargaMasivaRef.current.click();
    }
  };

  const descargarReporteErroresCarga = (errores) => {
    if (!Array.isArray(errores) || errores.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(errores);
    ws["!cols"] = [
      { wch: 10 }, // Fila
      { wch: 20 }, // Código evento
      { wch: 15 }, // DNI
      { wch: 35 }, // Motivo
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Errores");
    XLSX.writeFile(wb, `errores_carga_invitados_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const descargarReporteExitosCarga = (exitos, sufijo = "procesados") => {
    if (!Array.isArray(exitos) || exitos.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(exitos);
    ws["!cols"] = [
      { wch: 10 }, // Fila
      { wch: 20 }, // Código evento
      { wch: 15 }, // DNI
      { wch: 22 }, // Estado
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Procesados");
    XLSX.writeFile(
      wb,
      `procesados_carga_invitados_${sufijo}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const cargarInvitadosDesdeExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !rucEmpresa) return;

    setLoadingCargaMasiva(true);
    try {
      const dryRun = modoCargaMasivaRef.current === "preview";
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });

      if (!rows.length) {
        Swal.fire("Archivo vacío", "La plantilla no contiene filas para procesar.", "warning");
        return;
      }

      const parsedRows = rows.map((row, index) => {
        const normalized = Object.keys(row).reduce((acc, key) => {
          acc[normalizarHeader(key)] = row[key];
          return acc;
        }, {});

        const codigoEvento = String(
          normalized.codigo_evento || normalized.codigoevento || normalized.codigo || ""
        ).trim();
        const dni = String(normalized.dni || "").trim();
        return { codigoEvento, dni, fila: index + 2 };
      });

      const erroresDatosIncompletos = parsedRows
        .filter((r) => !r.codigoEvento || !r.dni)
        .map((r) => ({
          fila: r.fila,
          codigo_evento: r.codigoEvento || "",
          dni: r.dni || "",
          motivo: "Datos incompletos (falta codigo_evento o dni)",
        }));

      const invalidas = erroresDatosIncompletos.length;
      const validas = parsedRows.filter((r) => r.codigoEvento && r.dni);

      if (!validas.length) {
        Swal.fire("Sin datos válidos", "No hay filas válidas con codigo_evento y dni.", "warning");
        return;
      }

      const eventosByCodigoNormalizado = new Map(
        eventos
          .map((ev) => {
            const rawCode = String(ev.codigo || ev.id || "").trim();
            return [normalizarCodigo(rawCode), rawCode];
          })
          .filter(([, rawCode]) => Boolean(rawCode))
      );

      const usersByDni = new Map(
        usersEmpresa.map((u) => [String(u.dni || u.id || "").trim(), u])
      );

      const updatesByUserId = new Map();
      const seenPairs = new Set();

      let filasEventoNoExiste = 0;
      let filasDniNoExiste = 0;
      let filasYaInvitado = 0;
      let filasDuplicadas = 0;
      let invitacionesAgregadas = 0;

      const erroresEventoNoExiste = [];
      const erroresDniNoExiste = [];
      const erroresYaInvitado = [];
      const erroresDuplicadas = [];
      const exitosProcesables = [];

      validas.forEach(({ codigoEvento, dni, fila }) => {
        const codigoNormalizado = normalizarCodigo(codigoEvento);
        const codigoEventoReal = eventosByCodigoNormalizado.get(codigoNormalizado);
        if (!codigoEventoReal) {
          filasEventoNoExiste += 1;
          erroresEventoNoExiste.push({
            fila,
            codigo_evento: codigoEvento,
            dni,
            motivo: "El código de evento no existe",
          });
          return;
        }

        const user = usersByDni.get(dni);
        if (!user) {
          filasDniNoExiste += 1;
          erroresDniNoExiste.push({
            fila,
            codigo_evento: codigoEventoReal,
            dni,
            motivo: "DNI no encontrado en users",
          });
          return;
        }

        const par = `${user.id}::${codigoEventoReal}`;
        if (seenPairs.has(par)) {
          filasDuplicadas += 1;
          erroresDuplicadas.push({
            fila,
            codigo_evento: codigoEventoReal,
            dni,
            motivo: "Fila duplicada en el archivo",
          });
          return;
        }
        seenPairs.add(par);

        if (Array.isArray(user.eventos) && user.eventos.includes(codigoEventoReal)) {
          filasYaInvitado += 1;
          erroresYaInvitado.push({
            fila,
            codigo_evento: codigoEventoReal,
            dni,
            motivo: "El usuario ya estaba invitado a este evento",
          });
          return;
        }

        if (!updatesByUserId.has(user.id)) {
          updatesByUserId.set(user.id, { user, codigos: new Set() });
        }
        updatesByUserId.get(user.id).codigos.add(codigoEventoReal);
        exitosProcesables.push({
          fila,
          codigo_evento: codigoEventoReal,
          dni,
          estado: "Listo para agregar",
        });
        invitacionesAgregadas += 1;
      });

      const updates = Array.from(updatesByUserId.values());

      const aplicarInvitaciones = async () => {
        if (updates.length === 0) return;

        const CHUNK_SIZE = 400;
        for (let i = 0; i < updates.length; i += CHUNK_SIZE) {
          const chunk = updates.slice(i, i + CHUNK_SIZE);
          const batch = writeBatch(db);

          chunk.forEach(({ user, codigos }) => {
            batch.update(doc(db, "users", user.id), {
              eventos: arrayUnion(...Array.from(codigos)),
              updatedAt: serverTimestamp(),
            });
          });

          await batch.commit();
        }

        const codigosByUserId = new Map(
          updates.map(({ user, codigos }) => [user.id, Array.from(codigos)])
        );

        setUsersEmpresa((prev) =>
          prev.map((u) => {
            const codigos = codigosByUserId.get(u.id);
            if (!codigos) return u;
            return { ...u, eventos: Array.from(new Set([...(u.eventos || []), ...codigos])) };
          })
        );
      };

      const resumen =
        `Invitaciones listas para agregar: ${invitacionesAgregadas}\n` +
        `Filas inválidas: ${invalidas}\n` +
        `Evento no existe: ${filasEventoNoExiste}\n` +
        `DNI no encontrado: ${filasDniNoExiste}\n` +
        `Ya invitado: ${filasYaInvitado}\n` +
        `Filas duplicadas: ${filasDuplicadas}`;

      const erroresCarga = [
        ...erroresDatosIncompletos,
        ...erroresEventoNoExiste,
        ...erroresDniNoExiste,
        ...erroresYaInvitado,
        ...erroresDuplicadas,
      ];

      if (dryRun) {
        const result = await Swal.fire({
          title: "Simulación completada",
          text: resumen,
          icon: "info",
          showCancelButton: invitacionesAgregadas > 0,
          confirmButtonText: "Aplicar invitaciones",
          cancelButtonText: "Cerrar",
        });

        if (result.isConfirmed && invitacionesAgregadas > 0) {
          await aplicarInvitaciones();
          Swal.fire("Invitaciones aplicadas", `Se agregaron ${invitacionesAgregadas} invitaciones.`, "success");
          descargarReporteExitosCarga(
            exitosProcesables.map((x) => ({ ...x, estado: "Agregado" })),
            "aplicados"
          );
        } else if (exitosProcesables.length > 0) {
          descargarReporteExitosCarga(exitosProcesables, "simulacion");
        }

        if (erroresCarga.length > 0) {
          descargarReporteErroresCarga(erroresCarga);
        }
        return;
      }

      await aplicarInvitaciones();

      if (exitosProcesables.length > 0) {
        descargarReporteExitosCarga(
          exitosProcesables.map((x) => ({ ...x, estado: "Agregado" })),
          "aplicados"
        );
      }

      if (erroresCarga.length > 0) {
        descargarReporteErroresCarga(erroresCarga);
      }

      Swal.fire(
        "Carga masiva completada",
        resumen,
        "success"
      );
    } catch (err) {
      console.error("Error en carga masiva de invitados:", err);
      Swal.fire("Error", "No se pudo procesar la plantilla de invitados.", "error");
    } finally {
      setLoadingCargaMasiva(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "codigo" ? normalizarCodigo(value) : value,
    }));
  };

  const invitarUsuarioEvento = async (user) => {
    const codigoEvento = eventoInvitados?.codigo || eventoInvitados?.id;
    if (!codigoEvento || !user?.id) return;

    setSavingInvitado(true);
    try {
      await updateDoc(doc(db, "users", user.id), {
        eventos: arrayUnion(codigoEvento),
        updatedAt: serverTimestamp(),
      });

      setUsersEmpresa((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, eventos: Array.from(new Set([...(u.eventos || []), codigoEvento])) }
            : u
        )
      );
    } catch (err) {
      console.error("Error al invitar usuario al evento:", err);
      Swal.fire("Error", "No se pudo invitar al asistente.", "error");
    } finally {
      setSavingInvitado(false);
    }
  };

  const retirarUsuarioEvento = async (user) => {
    const codigoEvento = eventoInvitados?.codigo || eventoInvitados?.id;
    if (!codigoEvento || !user?.id) return;

    setSavingInvitado(true);
    try {
      await updateDoc(doc(db, "users", user.id), {
        eventos: arrayRemove(codigoEvento),
        updatedAt: serverTimestamp(),
      });

      setUsersEmpresa((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, eventos: (u.eventos || []).filter((ev) => ev !== codigoEvento) }
            : u
        )
      );
    } catch (err) {
      console.error("Error al retirar usuario del evento:", err);
      Swal.fire("Error", "No se pudo retirar al asistente.", "error");
    } finally {
      setSavingInvitado(false);
    }
  };

  // ── Guardar ─────────────────────────────────────────────────────────────────
  const guardarEvento = async (e) => {
    e.preventDefault();
    const codigo = normalizarCodigo(form.codigo || form.nombre);
    if (!codigo || !form.nombre?.trim() || !form.fecha) {
      Swal.fire("Datos incompletos", "Código, nombre y fecha son obligatorios.", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        codigo,
        nombre:         form.nombre.trim(),
        categoria:      form.categoria || CATEGORIAS[0],
        estado:         form.estado || "ACTIVO",
        fecha:          form.fecha,
        actualizadoEn:  serverTimestamp(),
        actualizadoPor: usuario?.email || "",
      };

      if (!editMode) {
        payload.creadoEn  = serverTimestamp();
        payload.creadoPor = usuario?.email || "";
      }

      await setDoc(
        doc(db, "eventos", rucEmpresa, "eventos", codigo),
        payload,
        { merge: true }
      );

      if (editMode && editandoCodigo && editandoCodigo !== codigo) {
        await deleteDoc(doc(db, "eventos", rucEmpresa, "eventos", editandoCodigo));
      }

      Swal.fire("Evento guardado", "El evento se registró correctamente.", "success");
      handleCloseModal();
      await cargarEventos();
    } catch (err) {
      console.error("Error al guardar evento:", err);
      Swal.fire("Error", "No se pudo guardar el evento.", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Eliminar ────────────────────────────────────────────────────────────────
  const eliminarEvento = async (evento) => {
    const codigo = evento.codigo || evento.id;
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Se eliminará el evento "${evento.nombre || codigo}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteDoc(doc(db, "eventos", rucEmpresa, "eventos", codigo));
      Swal.fire("Eliminado", "El evento fue eliminado correctamente.", "success");
      await cargarEventos();
    } catch (err) {
      console.error("Error al eliminar evento:", err);
      Swal.fire("Error", "No se pudo eliminar el evento.", "error");
    }
  };

  // ── Estadísticas ────────────────────────────────────────────────────────────
  const totalActivos = useMemo(
    () => eventos.filter((e) => e.estado === "ACTIVO").length,
    [eventos]
  );

  const hoy = new Date().toISOString().split("T")[0];
  const proximoEvento = useMemo(
    () => eventos.filter((e) => e.fecha >= hoy && e.estado === "ACTIVO").length,
    [eventos, hoy]
  );

  // ── Filtrado ────────────────────────────────────────────────────────────────
  const eventosFiltrados = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return [...eventos]
      .sort((a, b) => String(a.fecha || "").localeCompare(String(b.fecha || "")))
      .filter((e) => {
        const matchSearch =
          !term ||
          (e.nombre || "").toLowerCase().includes(term) ||
          (e.codigo || "").toLowerCase().includes(term) ||
          (e.categoria || "").toLowerCase().includes(term);
        const matchCat =
          filterCategoria === "TODAS" || e.categoria === filterCategoria;
        return matchSearch && matchCat;
      });
  }, [eventos, searchTerm, filterCategoria]);

  const codigoEventoSeleccionado = eventoInvitados?.codigo || eventoInvitados?.id || "";

  const usuariosInvitados = useMemo(() => {
    if (!codigoEventoSeleccionado) return [];
    return usersEmpresa.filter((u) => Array.isArray(u.eventos) && u.eventos.includes(codigoEventoSeleccionado));
  }, [usersEmpresa, codigoEventoSeleccionado]);

  const usuariosNoInvitados = useMemo(() => {
    if (!codigoEventoSeleccionado) return [];

    const term = searchInvitado.trim().toLowerCase();
    return usersEmpresa
      .filter((u) => !(Array.isArray(u.eventos) && u.eventos.includes(codigoEventoSeleccionado)))
      .filter((u) => {
        if (!term) return true;
        const nombre = `${u.name || ""} ${u.apellidos || ""}`.toLowerCase();
        const dni = String(u.dni || u.id || "").toLowerCase();
        return nombre.includes(term) || dni.includes(term);
      })
      .sort((a, b) => {
        const na = `${a.name || ""} ${a.apellidos || ""}`.toLowerCase();
        const nb = `${b.name || ""} ${b.apellidos || ""}`.toLowerCase();
        return na.localeCompare(nb);
      });
  }, [usersEmpresa, codigoEventoSeleccionado, searchInvitado]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
        >
          {/* Header Premium con degradado verde oscuro */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
              borderRadius: "15px 15px 0 0",
            }}
          >
            <div
              className="position-absolute end-0 top-50 translate-middle-y me-4"
              style={{ zIndex: 10 }}
            >
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaCalendarAlt className="me-3" />
                Gestión de Eventos
              </h2>
              <p className="mb-0 opacity-75">
                Configura y administra los eventos de asistencia de la empresa | RUC:{" "}
                {rucEmpresa}
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Acciones Rápidas */}
            <Card
              className="border-0 mb-4"
              style={{
                background: "linear-gradient(135deg,#fffcea,#fff9db)",
                borderRadius: 14,
                boxShadow: "0 2px 12px rgba(109,40,217,.07)",
              }}
            >
              <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap">
                <Button
                  onClick={() => handleOpenModal()}
                  disabled={!rucEmpresa || saving}
                  className="d-flex align-items-center gap-2 bg-white"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                    color: "#0d4f3c",
                    border: "1px solid #dee2e6",
                  }}
                >
                  <FaPlus size="16px" />
                  Nuevo Evento
                </Button>
                <Button
                  variant="outline-dark"
                  onClick={cargarEventos}
                  disabled={loading}
                  className="d-flex align-items-center gap-2 bg-white"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                  }}
                >
                  <FaSync className={loading ? "fa-spin" : ""} size="16px" />
                  Recargar
                </Button>

                <div className="vr mx-2" />

                <input
                  type="file"
                  hidden
                  ref={inputCargaMasivaRef}
                  accept=".xlsx, .xls"
                  onChange={cargarInvitadosDesdeExcel}
                />
                <Button
                  variant="outline-dark"
                  onClick={() => abrirSelectorCargaMasiva("preview")}
                  disabled={loadingCargaMasiva}
                  className="d-flex align-items-center gap-2 bg-white"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                  }}
                >
                  <FaLayerGroup size="16px" />
                  Carga Masiva (Simular)
                </Button>
                <Button
                  variant="outline-dark"
                  onClick={() => abrirSelectorCargaMasiva("commit")}
                  disabled={loadingCargaMasiva}
                  className="d-flex align-items-center gap-2 bg-white"
                  style={{
                    borderRadius: "10px",
                    padding: "10px 20px",
                    fontWeight: "bold",
                  }}
                >
                  <FaUserPlus size="16px" />
                  Carga Masiva (Aplicar)
                </Button>
              </Card.Body>
            </Card>

            {/* Fila de Filtros */}
            <div className="bg-white p-3 rounded-4 shadow-sm mb-4 border">
              <Row className="g-3 align-items-center">
                <Col md={12} className="text-end">
                  <Button
                    variant="primary"
                    onClick={cargarEventos}
                    disabled={loading || saving}
                    className="rounded-pill px-4"
                  >
                    <FaSync
                      className={loading ? "fa-spin me-2" : "me-2"}
                      size="16px"
                    />
                    Actualizar Lista
                  </Button>
                </Col>
              </Row>
            </div>

            {/* ── Estadísticas ── */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCalendarAlt size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{eventos.length}</h4>
                    <p className="mb-0 small opacity-75">Total Eventos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCalendarCheck size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{totalActivos}</h4>
                    <p className="mb-0 small opacity-75">Eventos Activos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaCalendarAlt size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{proximoEvento}</h4>
                    <p className="mb-0 small opacity-75">Próximos Activos</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                  <Card.Body
                    className="text-center py-4"
                    style={{
                      background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                      color: "white",
                      borderRadius: "15px",
                    }}
                  >
                    <FaSearch size="2rem" className="mb-2" />
                    <h4 className="fw-bold mb-0">{eventosFiltrados.length}</h4>
                    <p className="mb-0 small opacity-75">Resultados Filtro</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ── Búsqueda y filtro ── */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-2 align-items-center">
                  <Col md={7}>
                    <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                      <InputGroup.Text
                        className="border-0 px-4"
                        style={{
                          background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                          color: "white",
                        }}
                      >
                        <FaSearch />
                      </InputGroup.Text>
                      <Form.Control
                        className="border-0 py-3"
                        placeholder="Buscar por nombre, código o categoría..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ backgroundColor: "white" }}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={5}>
                    <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                      <InputGroup.Text
                        className="border-0 px-4"
                        style={{
                          background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                          color: "white",
                        }}
                      >
                        <FaFilter />
                      </InputGroup.Text>
                      <Form.Select
                        className="border-0 py-3"
                        value={filterCategoria}
                        onChange={(e) => setFilterCategoria(e.target.value)}
                        style={{ backgroundColor: "white" }}
                      >
                        <option value="TODAS">Todas las categorías</option>
                        {CATEGORIAS.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </Form.Select>
                    </InputGroup>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* ── Tabla principal ── */}
            <Card
              className="border-0 shadow-sm"
              style={{ borderRadius: "15px", overflow: "hidden" }}
            >
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Cargando eventos...</p>
                  </div>
                ) : eventosFiltrados.length > 0 ? (
                  <Table
                    hover
                    className="align-middle bg-white mb-0"
                    style={{ fontSize: "0.95rem" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #dee2e6",
                      }}
                    >
                      <tr>
                        <th className="py-3 px-4 text-secondary fw-semibold">Código</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Nombre</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Categoría</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Fecha</th>
                        <th className="py-3 px-4 text-secondary fw-semibold">Estado</th>
                        <th className="py-3 px-4 text-secondary fw-semibold text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventosFiltrados.map((evento) => {
                        const catColors = CATEGORIA_COLORS[evento.categoria] || CATEGORIA_COLORS["Otro"];
                        return (
                          <tr
                            key={evento.id || evento.codigo}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                          >
                            <td className="px-4 py-3 font-monospace fw-bold text-primary" style={{ fontSize: "0.85rem" }}>
                              {evento.codigo || evento.id}
                            </td>
                            <td className="px-4 py-3 fw-semibold">{evento.nombre}</td>
                            <td className="px-4 py-3">
                              <span
                                className="px-3 py-1 rounded-pill small fw-semibold"
                                style={{
                                  background: catColors.bg,
                                  color: "white",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <FaLayerGroup size={11} />
                                {evento.categoria || "N/A"}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                className="px-3 py-2"
                                style={{
                                  borderRadius: "8px",
                                  background: "rgba(59, 130, 246, 0.1)",
                                  color: "#1e40af",
                                  border: "1px solid rgba(59, 130, 246, 0.2)",
                                }}
                              >
                                {evento.fecha}
                              </Badge>
                            </td>
                            <td className="px-4 py-3">
                              <Badge
                                className="px-3 py-2"
                                style={{
                                  borderRadius: "8px",
                                  background:
                                    evento.estado === "ACTIVO"
                                      ? "rgba(5, 150, 105, 0.1)"
                                      : "rgba(107, 114, 128, 0.1)",
                                  color:
                                    evento.estado === "ACTIVO" ? "#065f46" : "#374151",
                                  border:
                                    evento.estado === "ACTIVO"
                                      ? "1px solid rgba(5, 150, 105, 0.2)"
                                      : "1px solid rgba(107, 114, 128, 0.2)",
                                }}
                              >
                                {evento.estado || "ACTIVO"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                variant="link"
                                className="text-success p-0 me-3"
                                onClick={() => handleOpenInvitadosModal(evento)}
                                title="Gestionar invitados"
                              >
                                <FaUserPlus size={18} />
                              </Button>
                              <Button
                                variant="link"
                                className="text-primary p-0 me-3"
                                onClick={() => handleOpenModal(evento)}
                              >
                                <FaEdit size={18} />
                              </Button>
                              <Button
                                variant="link"
                                className="text-danger p-0"
                                onClick={() => eliminarEvento(evento)}
                              >
                                <FaTrash size={18} />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <div className="text-center py-5">
                    <FaCalendarAlt size="4rem" className="text-muted opacity-25 mb-3" />
                    <h5 className="text-muted fw-semibold">
                      {searchTerm || filterCategoria !== "TODAS"
                        ? "No se encontraron resultados"
                        : "No hay eventos registrados"}
                    </h5>
                    <p className="text-muted">
                      {searchTerm || filterCategoria !== "TODAS"
                        ? "Intenta con otro término o categoría."
                        : "Crea el primer evento con el botón \"Nuevo Evento\"."}
                    </p>
                  </div>
                )}
              </div>
            </Card>

          </Card.Body>
        </Card>
      </Container>

      {/* ── Modal Crear / Editar ── */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <Modal.Title className="fw-bold">
            {editMode ? (
              <><FaEdit className="me-2" /> Editar Evento</>
            ) : (
              <><FaPlus className="me-2" /> Nuevo Evento</>
            )}
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={guardarEvento}>
          <Modal.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">
                    Código *
                  </Form.Label>
                  <Form.Control
                    name="codigo"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={form.codigo}
                    onChange={handleChange}
                    placeholder="Ej: dia_trabajador_2026"
                  />
                  <Form.Text className="text-muted">
                    Se genera automáticamente desde el nombre si lo dejas vacío.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">
                    Nombre *
                  </Form.Label>
                  <Form.Control
                    name="nombre"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={form.nombre}
                    onChange={handleChange}
                    placeholder="Día del Trabajador 2026"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">
                    Categoría *
                  </Form.Label>
                  <Form.Select
                    name="categoria"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={form.categoria}
                    onChange={handleChange}
                    required
                  >
                    {CATEGORIAS.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">
                    Fecha *
                  </Form.Label>
                  <Form.Control
                    type="date"
                    name="fecha"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={form.fecha}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="small fw-bold text-uppercase text-muted">
                    Estado
                  </Form.Label>
                  <Form.Select
                    name="estado"
                    className="border-0 shadow-sm py-2"
                    style={{ borderRadius: "10px" }}
                    value={form.estado}
                    onChange={handleChange}
                  >
                    {ESTADOS.map((est) => (
                      <option key={est} value={est}>{est}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer
            className="border-0 bg-light p-3"
            style={{ borderRadius: "0 0 15px 15px" }}
          >
            <Button
              variant="light"
              className="rounded-pill px-4 fw-semibold text-muted"
              onClick={handleCloseModal}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="rounded-pill px-4 fw-bold border-0 shadow-sm"
              style={{
                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
              }}
              disabled={saving || !rucEmpresa}
            >
              {saving ? (
                <><Spinner animation="border" size="sm" className="me-2" /> Guardando...</>
              ) : (
                <><FaSave className="me-2" />{editMode ? "Guardar Cambios" : "Crear Evento"}</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ── Modal Invitados ── */}
      <Modal
        show={showInvitadosModal}
        onHide={handleCloseInvitadosModal}
        size="xl"
        centered
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{
            background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <Modal.Title className="fw-bold">
            <FaUserPlus className="me-2" />
            Invitados: {eventoInvitados?.nombre || eventoInvitados?.codigo || "Evento"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {loadingInvitados ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="success" />
              <p className="mt-3 text-muted mb-0">Cargando asistentes de la empresa...</p>
            </div>
          ) : (
            <>
              <Row className="mb-3 g-3">
                <Col md={4}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                    <Card.Body className="py-3 text-center">
                      <h5 className="mb-0 fw-bold text-success">{usuariosInvitados.length}</h5>
                      <small className="text-muted">Invitados</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={8}>
                  <InputGroup className="shadow-sm rounded-pill overflow-hidden border">
                    <InputGroup.Text
                      className="border-0 px-4"
                      style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)", color: "white" }}
                    >
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      className="border-0 py-3"
                      placeholder="Buscar empleado por nombre o DNI para invitar..."
                      value={searchInvitado}
                      onChange={(e) => setSearchInvitado(e.target.value)}
                    />
                  </InputGroup>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md={6}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", height: "420px" }}>
                    <Card.Header className="bg-white fw-semibold">No invitados ({usuariosNoInvitados.length})</Card.Header>
                    <Card.Body style={{ overflowY: "auto" }}>
                      {usuariosNoInvitados.length === 0 ? (
                        <p className="text-muted mb-0">No hay resultados.</p>
                      ) : (
                        usuariosNoInvitados.map((u) => (
                          <div key={u.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                            <div>
                              <div className="fw-semibold">{`${u.name || ""} ${u.apellidos || ""}`.trim() || "Sin nombre"}</div>
                              <small className="text-muted">DNI: {u.dni || u.id}</small>
                            </div>
                            <Button
                              size="sm"
                              className="rounded-pill border-0"
                              style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 100%)" }}
                              disabled={savingInvitado}
                              onClick={() => invitarUsuarioEvento(u)}
                            >
                              <FaUserPlus className="me-1" /> Invitar
                            </Button>
                          </div>
                        ))
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", height: "420px" }}>
                    <Card.Header className="bg-white fw-semibold">Invitados ({usuariosInvitados.length})</Card.Header>
                    <Card.Body style={{ overflowY: "auto" }}>
                      {usuariosInvitados.length === 0 ? (
                        <p className="text-muted mb-0">Aún no hay invitados para este evento.</p>
                      ) : (
                        usuariosInvitados
                          .sort((a, b) => `${a.name || ""} ${a.apellidos || ""}`.localeCompare(`${b.name || ""} ${b.apellidos || ""}`))
                          .map((u) => (
                            <div key={u.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                              <div>
                                <div className="fw-semibold">{`${u.name || ""} ${u.apellidos || ""}`.trim() || "Sin nombre"}</div>
                                <small className="text-muted">DNI: {u.dni || u.id}</small>
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="rounded-pill"
                                disabled={savingInvitado}
                                onClick={() => retirarUsuarioEvento(u)}
                              >
                                <FaUserMinus className="me-1" /> Retirar
                              </Button>
                            </div>
                          ))
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="border-0 bg-light p-3" style={{ borderRadius: "0 0 15px 15px" }}>
          <input
            ref={inputCargaMasivaRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
            onChange={cargarInvitadosDesdeExcel}
          />
          <Button
            variant="outline-primary"
            className="rounded-pill px-3"
            onClick={descargarPlantillaInvitados}
            disabled={loadingCargaMasiva || savingInvitado}
          >
            Descargar plantilla
          </Button>
          <Button
            variant="outline-secondary"
            className="rounded-pill px-3"
            onClick={() => abrirSelectorCargaMasiva("preview")}
            disabled={loadingCargaMasiva || savingInvitado}
          >
            Simular carga (Excel)
          </Button>
          <Button
            className="rounded-pill px-3 border-0"
            style={{ background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)" }}
            onClick={() => abrirSelectorCargaMasiva("commit")}
            disabled={loadingCargaMasiva || savingInvitado}
          >
            {loadingCargaMasiva ? (
              <><Spinner animation="border" size="sm" className="me-2" /> Cargando...</>
            ) : (
              <>Invitar multi-evento (Excel)</>
            )}
          </Button>
          <Button
            variant="light"
            className="rounded-pill px-4 fw-semibold text-muted"
            onClick={handleCloseInvitadosModal}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventosAsistencia;
